import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import {
  Channel,
  Chat,
  Window
} from 'stream-chat-react';
import get from 'lodash/get';

import { PageSpinner } from '../../shared-components/decorators/spinner';

import { useStreamChat } from '../utils/use-stream-chat';
import { StreamMessageInput } from './stream-message-input/stream-message-input';
import { StreamMessageList } from './stream-message/stream-message-list';
import { StreamMaintenanceBanner } from './stream-banners/stream-maintenance-banner';
import { ChatContextProvider } from '../utils/chat-context';
import { StreamMessage } from './stream-message/stream-message';
import { noop } from '../utils/utils';
import { AttachmentsAnnouncement } from './stream-banners/attachments-announcement/attachments-announcement';
import { DateSeparator } from './stream-message/date-separator';

const StreamChat = ({
  channelId,
  profile,
  responsiveness,
  streamApiKey,
  generateMessageMetadata,
  thread,
  fetchBooking,
  accessToken,
  bookingId,
  bookingState,
  viewAs,
  showFlaggingSuccessBanner,
  intl
}) => {
  const {
    client,
    error,
    activeChannel,
    isConnecting,
    isConnected,
    isFetchingChannel,
    addInputErrorIds,
    removeInputErrorIds,
    clearInputErrors,
    inputErrors
  } = useStreamChat(channelId, streamApiKey, profile, accessToken, generateMessageMetadata, fetchBooking, intl);

  const [savedMessageText, setSavedMessageText] = useState('');
  const savedAttachmentsRef = useRef([]);
  const savedAttachments = savedAttachmentsRef.current;
  const setSavedAttachments = (value) => {
    savedAttachmentsRef.current = value;
  };

  const ssoId = get(profile, 'data.SSOid');
  const displayResponsiveness = get(thread, 'display_responsiveness');
  const isLoading = (!client && !error) || !isConnected || isConnecting || isFetchingChannel;

  if (isLoading) {
    return <PageSpinner additionalStyle="gray"/>;
  }

  if (error) {
    return (
      <StreamMaintenanceBanner/>
    );
  }

  return (
    <ChatContextProvider
      responsiveness={responsiveness}
      ssoId={ssoId}
      profile={profile}
      addInputErrorIds={addInputErrorIds}
      removeInputErrorIds={removeInputErrorIds}
      clearInputErrors={clearInputErrors}
      inputErrors={inputErrors}
      displayResponsiveness={displayResponsiveness}
      bookingId={bookingId}
      bookingState={bookingState}
      viewAs={viewAs}
      savedMessageText={savedMessageText}
      setSavedMessageText={setSavedMessageText}
      savedAttachments={savedAttachments}
      setSavedAttachments={setSavedAttachments}
      generateMessageMetadata={generateMessageMetadata}
      showFlaggingSuccessBanner={showFlaggingSuccessBanner}
      accessToken={accessToken}
      client={client}
    >
      <AttachmentsAnnouncement/>
      <div className="StreamChat">
        <Chat client={client}>
          {activeChannel && (
            <Channel
              channel={activeChannel}
              DateSeparator={DateSeparator}
              TypingIndicator={noop}
              MessageSystem={StreamMessage}
              activeUnreadHandler={noop}
              LoadingIndicator={noop}
            >
              <Window>
                <div className="MessageListContainer">
                  <StreamMessageList/>
                  <div className="gradient-footer" />
                </div>
                <StreamMessageInput/>
              </Window>
            </Channel>
          )}
        </Chat>
      </div>
    </ChatContextProvider>
  );
};

StreamChat.propTypes = {
  channelId: PropTypes.string,
  profile: PropTypes.object,
  responsiveness: PropTypes.object,
  thread: PropTypes.object,
  streamApiKey: PropTypes.string,
  generateMessageMetadata: PropTypes.func,
  showFlaggingSuccessBanner: PropTypes.func,
  fetchBooking: PropTypes.func,
  accessToken: PropTypes.string,
  bookingId: PropTypes.string,
  bookingState: PropTypes.object,
  viewAs: PropTypes.string,
  intl: intlShape
};

exports.StreamChat = injectIntl(StreamChat);
